

import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Ensure you have axios installed
import AccessDenied from './AccessDenied';
import { base_url } from '../api/index';
import { useI18n } from '../i18n/I18nContext';

const getHeaders = () => ({
  headers: {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

function OncedenOde({ odersId, setrefreshfetch }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState<any>('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [previousPayments, setPreviousPayments] = useState<any[]>([]);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${base_url}/order/${odersId.id}/prepayments`, getHeaders());
      setPreviousPayments(response.data);
      setrefreshfetch(false);
    } catch (error: any) {
      console.error('Error fetching previous payments:', error);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [odersId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate that amount is provided
    if (amount <= 0) {
      alert(t("prepay.amountPositive"));
      return;
    }

    try {
      const currentDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format

      await axios.post(`${base_url}/order/${odersId.id}/prepayments`, {
        amount,
        type: paymentMethod === 'cash' ? 'cash' : 'bank',
        date: currentDate,
      }, getHeaders());

      setrefreshfetch(true); // Notify parent to refresh data
      fetchPayments(); // Fetch updated payments
      setAmount('');
      setPaymentMethod('cash');
    } catch (error: any) {
      if (error.response && error.response.status === 403 && error.response.data.message === "Forbidden") {
        setAccessDenied(true); // Set access denied if response status is 403
      }
      if (error.response && error.response.status === 400 && error.response.data.message === "Order is already fully paid.") {
        setAccessDenied(true); // Set access denied if response status is 403
      }
      if (error.response && error.response.status === 400 && error.response.data.message === "Amount exceeds the remaining balance.") {
        alert(t("prepay.exceedsBalance"));
      }
      if (error.response && error.response.status === 404 && error.response.data.message === "Order not found.") {
        alert(t("prepay.orderNotFound"));
      }
      else {
        console.error('Error submitting payment:', error);
      }
    }
  };

  const kalanmebleg = (total, onceden) => {
    const result = total - onceden;
    return parseFloat(result.toFixed(1));
  };

  const handleDeleteOrderPrepare = async (prepareId) => {
    try {
      await axios.delete(`${base_url}/order/${odersId.id}/prepayments/${prepareId}`, getHeaders());
      setrefreshfetch(true); // Notify parent to refresh data
      fetchPayments(); // Fetch updated payments
    } catch (error: any) {
      if (error.response && error.response.status === 403 && error.response.data.message === "Forbidden") {
        setAccessDenied(true); // Set access denied if response status is 403
      } else {
        console.error('Error deleting payment:', error);
      }
    }
  };

  if (accessDenied) return <AccessDenied onClose={setAccessDenied} />;

  return (
    <div className="bg-gray-100 flex flex-col items-center justify-center min-h-screen p-6">
      <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-lg">
        <div className="border-t border-gray-300 mb-4"></div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex-1 mr-2">
                <label htmlFor="amount" className="block text-gray-700">{t("prepay.amountAzn")}</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (Number(value) >= 0) {
                      setAmount(value);
                    }
                  }}
                  min="0"
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex-1 ml-2">
                <label htmlFor="payment-method" className="block text-gray-700">{t("prepay.payment")}</label>
                <select
                  id="payment-method"
                  name="payment-method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">{t("checkout.pay.cash")}</option>
                  <option value="transfer">{t("prepay.bankTransfer")}</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </form>

        {/* List of previous payments */}
        <div className="mt-8">
          <h4 className="text-lg font-semibold mb-4">{t("prepay.previous")}</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("prepay.amountAzn")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("prepay.payment")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("common.date")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("common.delete")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previousPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payment.amount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payment.type === "cash" ? t("checkout.pay.cash") : t("prepay.bankTransfer")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payment.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleDeleteOrderPrepare(payment.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <i className="fa-solid fa-trash-can"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>

        {/* Total Sum */}
        <div className="mt-6 flex justify-end font-semibold text-gray-700">
          <span className="text-lg">{t("gider.total")}: </span>
          <span className="text-lg ml-2">{odersId.total_price ?? 0} AZN</span>
        </div>
        <div className="mt-6 flex justify-end font-semibold text-green-700">
          <span className="text-lg">{t("siparis.alreadyPaid")}: </span>
          <span className="text-lg ml-2">{odersId.total_prepayment ?? 0} AZN</span>
        </div>
        <div className="mt-6 flex justify-end font-semibold text-red-700">
          <span className="text-lg">{t("siparis.remaining")}: </span>
          <span className="text-lg ml-2">{kalanmebleg(odersId.total_price, odersId.total_prepayment)} AZN</span>
        </div>
      </div>
    </div>
  );
}

export default OncedenOde;


